import React, { useState } from 'react';
import { LinkField, Text, Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { EventFields, EventProps } from 'components/event/Event';
import sitecoreStyles from 'src/helpers/sitecoreStyles';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import dynamic from 'next/dynamic';
import config from 'temp/config';
import LazyLoadComponent from 'components/shared/lazy-load/LazyLoadComponent';
import PaginationFn from '../pagination/Pagination';
import { useMediaQuery } from '@mui/material';

export interface EventsListFields {
  id: string;
  title: {
    jsonValue: Field<string>;
  };
  link: {
    jsonValue: LinkField;
  };
  events: {
    targetItems: EventFields[];
  };
}

type EventsListProps = {
  params: { [key: string]: string; styles: string };
  fields?: {
    data: {
      datasource: EventsListFields;
    };
  };
};

export const Default = (props: EventsListProps): JSX.Element => {
  const id = props.params.RenderingIdentifier;
  const datasource = props.fields?.data?.datasource;
  const [editingMode] = useState(isEditorActive());
  const editingmode = isEditorActive();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1024px)');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = isMobile ? 1 : isTablet ? 2 : datasource?.events.targetItems.length || 0;
  const step = isTablet ? 2 : itemsPerPage;
  const totalItems = datasource?.events.targetItems.length || 0;
  const totalPages = isMobile || isTablet ? Math.ceil(totalItems / itemsPerPage) : 1;

  const currentItems = isMobile
    ? datasource?.events.targetItems.slice(currentPage - 1, currentPage + 1) || []
    : isTablet
    ? datasource?.events.targetItems.slice(
        (currentPage - 1) * step,
        (currentPage - 1) * step + itemsPerPage + 1
      ) || []
    : datasource?.events.targetItems.slice(0, datasource?.events.targetItems.length) || [];

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  let DynamicTertiary;
  if (editingmode) {
    // Static import when in editing mode
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    DynamicTertiary = require('components/link/Link').Tertiary;
  } else {
    // Dynamic import when not in editing mode
    DynamicTertiary = dynamic(() => import('components/link/Link').then((mod) => mod.Tertiary), {
      ssr: !!config.dynamicImportSsr,
    });
  }

  let DynamicEvent;
  if (editingmode) {
    // Static import when in editing mode
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    DynamicEvent = require('components/event/Event').Default;
  } else {
    // Dynamic import when not in editing mode
    DynamicEvent = dynamic(() => import('components/event/Event').then((mod) => mod.Default), {
      ssr: !!config.dynamicImportSsr,
    });
  }
  if (datasource) {
    return (
      <LazyLoadComponent componentId="EventList">
        <div
          className={`events-list component--with-paddings ${sitecoreStyles(props.params?.styles)}`}
          id={id ? id : undefined}
        >
          <div
            className={`events-list__container ${
              datasource?.events.targetItems?.length === 1 ? 'single-card' : ''
            }`}
          >
            <div className="events-list__title-container">
              <Text tag="h3" className="events-list__title" field={datasource.title.jsonValue} />
            </div>
            <div
              className={`events-list__cards-container ${
                datasource?.events.targetItems?.length === 1 ? 'single-card' : ''
              }`}
            >
              {currentItems.map((item) => {
                const eventProps: EventProps = {
                  fields: {
                    data: {
                      datasource: item,
                    },
                  },
                  params: {},
                };
                return <DynamicEvent key={item.id} {...eventProps} />;
              })}
            </div>
            {(datasource.link.jsonValue.value.href || editingMode) && !(isMobile || isTablet) && (
              <div className="events-list__cta-container ">
                <DynamicTertiary fields={{ Link: datasource.link.jsonValue }} params={{}} />
              </div>
            )}
            {(isMobile || isTablet) && !(datasource?.events.targetItems?.length === 1) && (
              <div className="events-list__pagination">
                <PaginationFn
                  count={totalPages}
                  page={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </LazyLoadComponent>
    );
  }
  return <EventsListDefaultComponent {...props} />;
};

export const EventsListDefaultComponent = (props: EventsListProps): JSX.Element => {
  const [editingMode] = useState(isEditorActive());
  return (
    <>
      {editingMode && (
        <div
          className={`events-list component--with-paddings ${sitecoreStyles(props.params?.styles)}`}
        >
          <span className="is-empty-hint">Events List</span>
        </div>
      )}
    </>
  );
};
